// ========================================
// STYLES
// ========================================

import '../styles/main/main.scss'
import '../styles/main/projects.scss'
import '../styles/main/notes.scss'
import '../styles/responsivephone/phone_main.scss'
import '../styles/error.scss'


// ========================================
// BASIC
// ========================================

import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'


// ========================================
// COMPONENTS
// ========================================

import FolderWindow from '../components/main/folderwindow'
import Footer from '../components/footer/footer'
import Notes from '../components/main/notes'
import Note from '../components/main/note'
import Music from '../components/main/music'
import Modal from '../components/main/modal'
import Bin from '../components/main/bin'
import Loading from '../components/loading'
import PhoneHeader from '../components/phoneresponsive/phone_header'


// ========================================
// HOOKS
// ========================================

import { useFetch } from '../hooks/fetch'
import { useFetchMusic } from '../hooks/fetch_music'


// ========================================
// IMAGES - PC
// ========================================

import Trash from '../assets/trash.svg'
import About from '../assets/about.svg'
import ProfilePic from '../assets/universe_girl_pic.jpg'

import Note1 from '../assets/note_1_img.jpg'
import Note2 from '../assets/note_2_img.jpg'
import Note3 from '../assets/note_3_img.jpg'


// ========================================
// IMAGES - PHONE
// ========================================

import FileIcon from '../assets/file.svg'
import AboutMe from '../assets/about_me_phone.svg'
import SwitchGirl from '../assets/girl_switch.jpg'
import PhoneGirl from '../assets/girl_phone.jpg'
import NotesIcon from '../assets/cv_notes_icon.svg'
import BottomCover from '../assets/phone_bottom_cover.gif'
import Home from '../assets/back_home.png'

// ========================================
// LANGUAGE
// ========================================

import { useLanguage } from '../context/language_context'
import { translate } from '../utils/translate'


function Main() {

    // ========================================
    // DATA
    // ========================================

    const { data, error } = useFetch('/db/projects.json?v=${Date.now()}')

    const { songs, songsError } = useFetchMusic('/db/music.json')

    // ========================================
    // LANGUAGE
    // ========================================

    const { language, resetLanguage } = useLanguage()


    // ========================================
    // LOADING
    // ========================================

    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {

        if (data && songs) {
            setIsLoading(false)
        }

    }, [data, songs])


    // ========================================
    // PROJECT MODAL
    // ========================================

    // Contient directement l'objet du projet sélectionné.
    // null = aucun projet ouvert.

    const [selectedProject, setSelectedProject] = useState(null)


    const openProject = (project) => {

        setSelectedProject(project)

    }


    const closeProject = () => {

        setSelectedProject(null)

    }


    // ========================================
    // FOLDERS
    // ========================================

    // Liste de toutes les fenêtres de dossiers ouvertes.
    //
    // Exemple :
    //
    // [
    //     {
    //         folder: {...},
    //         path: ['Esport et jeux vidéo']
    //     }
    // ]

    const [openFolders, setOpenFolders] = useState([])


    const openFolder = (folder, path) => {

        setOpenFolders((previousFolders) => {

            // Vérifie si ce dossier est déjà ouvert

            const alreadyOpen = previousFolders.some(
                (openFolder) => openFolder.folder.id === folder.id
            )


            // S'il est déjà ouvert, on ne fait rien

            if (alreadyOpen) {
                return previousFolders
            }


            // Sinon on ajoute une nouvelle fenêtre

            return [
                ...previousFolders,
                {
                    folder: folder,
                    path: path
                }
            ]

        })

    }


    const closeFolder = (folderId) => {

        setOpenFolders((previousFolders) =>

            previousFolders.filter(
                (openFolder) => openFolder.folder.id !== folderId
            )

        )

    }


    // ========================================
    // BIN
    // ========================================

    const [isBinModalOpen, setIsBinModalOpen] = useState(false)


    const openBinModal = () => {

        setIsBinModalOpen(true)

    }


    const closeBinModal = () => {

        setIsBinModalOpen(false)

    }


    // ========================================
    // MUSIC
    // ========================================

    const [volume, setVolume] = useState(0.5)

    const audioElementRef = useRef(null)


    // ========================================
    // LOADING / ERRORS
    // ========================================

    if (isLoading) {

        return <Loading />

    }


    if (songsError) {

        return <span>Error fetching songs data.</span>

    }


    if (error) {

        return <span>Il y a un problème avec les projets.</span>

    }


    if (!songs) {

        return <span>Chargement des musiques...</span>

    }


    if (!data) {

        return <span>Chargement des projets...</span>

    }


    // ========================================
    // ROOT PROJECT FOLDER
    // ========================================

    // projects.json représente maintenant le contenu
    // du dossier principal "Projets".

    const rootFolder = {

        id: 'projects',
        name: 'Projets',
        type: 'folder',
        children: data

    }


    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="main_overall_container">


            {/* ========================================
                PC VERSION
            ======================================== */}

            <section className="main_pc">


                <div className="container main_container">


                    {/* ========================================
                        LEFT
                        MAIN PROJECT WINDOW
                    ======================================== */}

                    <div className="main_left">

                        <FolderWindow
                            folder={rootFolder}
                            path={[]}
                            onOpenFolder={openFolder}
                            onOpenProject={openProject}
                            isRoot={true}
                        />

                    </div>


                    {/* ========================================
                        CENTER
                    ======================================== */}

                    <section className="main_center">


                        <div className="center_icons">

                            {/* BIN */}

                            <div
                                className="trash_icon"
                                onClick={openBinModal}
                            >

                                <img
                                    src={Trash}
                                    alt={
                                        language === 'fr'
                                            ? "Icône corbeille"
                                            : "Trash icon"
                                    }
                                />

                                <p>
                                    {language === 'fr'
                                        ? 'Corbeille'
                                        : 'Recycle Bin'}
                                </p>

                            </div>


                            {/* CV */}

                            <Link
                                to="/cv"
                                className="about_icon"
                            >

                                <img
                                    src={About}
                                    alt={
                                        language === 'fr'
                                            ? "Icône CV"
                                            : "Resume icon"
                                    }
                                />

                                <p>
                                    {language === 'fr'
                                        ? 'Mon CV'
                                        : 'My Resume'}
                                </p>

                            </Link>

                        </div>


                        {/* CHARACTER */}

                        <div className="center">

                            <img
                                src={ProfilePic}
                                alt="Dessin féminin en pixel art"
                            />

                        </div>


                    </section>


                    {/* ========================================
                        RIGHT
                    ======================================== */}

                    <section className="main_right">


                        <Notes>

                            <Note
                                text={
                                    language === 'fr'
                                        ? "To do : Finir le portfolio !"
                                        : "To do: Finish the portfolio!"
                                }
                                img={Note1}
                                className="note_1"
                            />

                            <Note
                                text={
                                    language === 'fr'
                                        ? "Halloween 2026 en approche !"
                                        : "Halloween 2026 is coming!"
                                }
                                img={Note2}
                                className="note_2"
                            />

                            <Note
                                text={
                                    language === 'fr'
                                        ? "To do : Organiser une exposition avec les dessins de Moonloading !"
                                        : "To do: Organize an exhibition featuring the Moonloading illustrations!"
                                }
                                img={Note3}
                                className="note_3"
                            />

                        </Notes>


                        <Music
                            songs={songs}
                            audioElementRef={audioElementRef}
                        />


                    </section>


                </div>


                {/* ========================================
                    OPEN FOLDER WINDOWS
                ======================================== */}

                {openFolders.map(({ folder, path }) => (

                    <FolderWindow
                        key={folder.id}
                        folder={folder}
                        path={path}
                        onOpenFolder={openFolder}
                        onOpenProject={openProject}
                        onClose={() => closeFolder(folder.id)}
                        isRoot={false}
                    />

                ))}


                {/* ========================================
                    PROJECT MODAL
                ======================================== */}

                {selectedProject && (

                    <Modal
                        closeModal={closeProject}

                        name={translate(
                            selectedProject.name,
                            language
                        )}

                        description={translate(
                            selectedProject.description,
                            language
                        )}

                        projectstate={translate(
                            selectedProject.projectstate,
                            language
                        )}

                        objectives={translate(
                            selectedProject.objectives,
                            language
                        )}

                        langages={translate(
                            selectedProject.langages,
                            language
                        )}

                        creationdate={translate(
                            selectedProject.creationdate,
                            language
                        )}

                        github={selectedProject.github}

                        images={selectedProject.images}
                    />

                )}


                {/* ========================================
                    BIN MODAL
                ======================================== */}

                {isBinModalOpen && (

                    <Bin
                        closeBinModal={closeBinModal}
                    />

                )}


                {/* ========================================
                    FOOTER
                ======================================== */}

                <Footer
                    audioElementRef={audioElementRef}
                    volume={volume}
                    setVolume={setVolume}
                />


            </section>


            {/* ========================================
            PHONE VERSION
            ======================================== */}

            <section className="main_phone">

                <div className="main_phone_container">

                    <div className="phone_header_and_main_content">

                        <PhoneHeader />

                        <section className="phone_main_content">

                            {/* PROJECTS */}

                            <Link
                                to="/persoprojects"
                                className="phone_app"
                            >

                                <img
                                    src={FileIcon}
                                    alt={
                                        language === 'fr'
                                            ? "Icône de dossier"
                                            : "Folder icon"
                                    }
                                />

                                {language === 'fr'
                                    ? 'Mes projets'
                                    : 'My projects'}

                            </Link>


                            {/* CV */}

                            <Link
                                to="/cv"
                                className="phone_app"
                            >

                                <img
                                    src={AboutMe}
                                    alt={
                                        language === 'fr'
                                            ? "Icône de personnage"
                                            : "Character icon"
                                    }
                                />

                                {language === 'fr'
                                    ? 'Mon CV'
                                    : 'My resume'}

                            </Link>


                            {/* GIRL */}

                            <img
                                src={PhoneGirl}
                                alt=""
                                className="girl_pic"
                            />

                            <img
                                src={SwitchGirl}
                                alt=""
                                className="girl_pic"
                            />


                            {/* NOTES */}

                            <Link
                                to="/phonenotes"
                                className="phone_app"
                            >

                                <img
                                    src={NotesIcon}
                                    alt={
                                        language === 'fr'
                                            ? "Icône de notes"
                                            : "Notes icon"
                                    }
                                />

                                {language === 'fr'
                                    ? 'Mes notes'
                                    : 'My notes'}

                            </Link>

                            <Link
                                to="/"
                                className="phone_app"
                                onClick={resetLanguage}
                            >

                                <img
                                    src={Home}
                                    alt={
                                        language === 'fr'
                                            ? "Icône de maison"
                                            : "Home icon"
                                    }
                                    
                                />

                                {language === 'fr'
                                    ? 'Retour à l’accueil'
                                    : 'Back to home'}
                            </Link>


                            {/* BOTTOM GIF */}

                            <img
                                src={BottomCover}
                                className="phone_bottom_cover"
                                alt=""
                            />

                        </section>

                    </div>


                    <Music
                        songs={songs}
                        audioElementRef={audioElementRef}
                    />

                </div>

            </section>


        </div>

    )

}


export default Main